// Records an installment against an invoice — the money screen's "רישום תשלום".
//
// FOR MONEY THAT ARRIVES AFTER THE CHARGE WAS RECORDED — a later payment, or the next
// installment. A charge the client had already paid when it was recorded carries its
// payment from the recording dialog itself (RecordChargeModal's "has the client paid?"),
// and the amount / date / method fields are the same component in both (PaymentFields).
//
// TWO MORE THINGS THAN AN AMOUNT, because a charge raised at entry arrives here with
// nothing else done to it:
//
//   WHO COLLECTED IT — a fact about THIS PAYMENT (migration 068). The standing gap
//     credits each payment to the partner who banked it, so one charge paid in
//     instalments may be collected by both partners, one instalment each. Prefilled with
//     the charge's issuer and asked on every payment, never locked; it changes nothing
//     about the charge itself. A payment credited to the wrong partner is undone from the
//     charge's payments list and recorded again.
//
//   THE SCAN. It belongs to the invoice, not the payment (one invoice may be paid in
//     several installments and has one document), so it is uploaded to the invoice after
//     the payment is saved. If the upload fails the payment still stands, and the dialog
//     says exactly that rather than reporting the whole act as failed.
import { useState } from 'react';
import Modal from './Modal';
import { Field, Select, Textarea } from './Form';
import { ErrorText } from './Loader';
import PaymentFields from './PaymentFields';
import he from '../i18n/he';
import useAsync from '../hooks/useAsync';
import { paymentsApi, invoicesApi, authApi } from '../api';

export default function PaymentModal({ invoice, onClose, onSaved }) {
  const { data: partners } = useAsync(() => authApi.partners());
  const [form, setForm] = useState({
    amount: invoice.balance ?? '',
    payment_date: '',
    method: 'transfer',
    notes: '',
    collected_by_partner_id: invoice.issuingPartnerId || '',
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fileFailed, setFileFailed] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  // An undecided charge (074) prefills nobody: the collector is chosen, and Save waits for it.
  // "Choose" is shown rather than the first partner, which a select would otherwise display
  // while sending nothing.
  const partnerOptions = [
    ...(invoice.issuingPartnerId ? [] : [{ value: '', label: he.common.choose }]),
    ...(partners || []).map((p) => ({ value: p.id, label: p.name })),
  ];

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await paymentsApi.create({
        invoice_id: invoice.id,
        ...form,
        collected_by_partner_id: Number(form.collected_by_partner_id) || null,
      });
    } catch (e) {
      setError(e);
      setSaving(false);
      return;
    }
    if (file) {
      try {
        const body = new FormData();
        body.append('file', file);
        await invoicesApi.uploadFile(invoice.id, body);
      } catch (e) {
        // The payment is recorded; only the document is missing. Say so, and let the
        // partner close the dialog knowing the money side is done.
        setFileFailed(true);
        setSaving(false);
        return;
      }
    }
    onSaved();
  };

  // After a failed upload the payment already exists, so the dialog's only honest
  // action is to close — re-submitting would record the payment twice.
  if (fileFailed) {
    return (
      <Modal title={he.payment.title} onClose={onSaved} onSave={onSaved} saveLabel={he.common.close}>
        <p className="danger">{he.payment.fileFailed}</p>
      </Modal>
    );
  }

  return (
    <Modal title={he.payment.title} onClose={onClose} onSave={submit} saveDisabled={saving || !form.collected_by_partner_id}>
      <PaymentFields form={form} set={set} />
      <Select
        label={he.payment.collectedBy}
        value={form.collected_by_partner_id}
        onChange={set('collected_by_partner_id')}
        options={partnerOptions}
      />
      <Field label={he.payment.file}>
        <input type="file" onChange={(e) => setFile(e.target.files[0] || null)} />
      </Field>
      <Textarea label={he.payment.notes} value={form.notes} onChange={set('notes')} />
      {error && <ErrorText error={error} />}
    </Modal>
  );
}
